import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentService, batchService } from '../services/api';
import { useApp } from '../context/AppContext';
import Modal from '../components/Modal';

const Students = () => {
  const { currentUser, notify, startImpersonation } = useApp();
  const isAdmin = currentUser.role === 'admin';
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loginFilter, setLoginFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [batchToEnroll, setBatchToEnroll] = useState('');

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBatchId, setBulkBatchId] = useState('');
  const [bulkEnrolling, setBulkEnrolling] = useState(false);

  const loadStudents = useCallback(
    () => studentService.list().then(setStudents).catch((err) => notify(err.message, 'error')),
    [notify]
  );

  useEffect(() => {
    loadStudents();
    if (isAdmin) {
      batchService.list().then(setBatches).catch((err) => notify(err.message, 'error'));
    }
  }, [loadStudents, notify, isAdmin]);

  const teachers = Array.from(
    new Set(students.flatMap((s) => s.batches.map((b) => b.teacher).filter(Boolean)))
  ).sort();

  const filtered = students
    .filter((s) => s.name.toLowerCase().includes(search.trim().toLowerCase()))
    .filter((s) => !typeFilter || s.customerType === typeFilter)
    .filter((s) => !loginFilter || (loginFilter === 'enabled' ? s.hasLogin : !s.hasLogin))
    .filter((s) => !teacherFilter || s.batches.some((b) => b.teacher === teacherFilter));

  const columnCount = isAdmin ? 10 : 9;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selectedIds.has(s.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((s) => next.delete(s.id));
      } else {
        filtered.forEach((s) => next.add(s.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditEmail(s.email || '');
    setBatchToEnroll('');
  };

  const cancelEdit = () => setEditingId(null);

  const handleEnroll = async (e, studentId) => {
    e.preventDefault();
    if (!batchToEnroll) return;
    try {
      await batchService.enroll(batchToEnroll, studentId);
      notify('Student enrolled', 'success');
      setBatchToEnroll('');
      loadStudents();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleUnenroll = async (studentId, batch) => {
    const confirmed = window.confirm(`Remove this student from "${batch.name}"?`);
    if (!confirmed) return;
    try {
      await batchService.unenroll(batch.id, studentId);
      notify(`Removed from ${batch.name}`, 'success');
      loadStudents();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleBulkEnroll = async (e) => {
    e.preventDefault();
    if (!bulkBatchId || selectedIds.size === 0) return;
    setBulkEnrolling(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => batchService.enroll(bulkBatchId, id)));
    const failures = results.filter((r) => r.status === 'rejected').length;
    const succeeded = ids.length - failures;
    if (failures === 0) {
      notify(`Enrolled ${succeeded} student${succeeded === 1 ? '' : 's'}`, 'success');
    } else {
      notify(`Enrolled ${succeeded}, ${failures} failed`, succeeded > 0 ? 'success' : 'error');
    }
    setBulkEnrolling(false);
    setBulkBatchId('');
    setSelectedIds(new Set());
    loadStudents();
  };

  const handleSaveEmail = async (e, id) => {
    e.preventDefault();
    try {
      await studentService.updateEmail(id, editEmail);
      notify('Email updated', 'success');
      setEditingId(null);
      loadStudents();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleEnableLogin = async (s) => {
    try {
      await studentService.enableLogin(s.id);
      notify(`Login enabled for ${s.name}`, 'success');
      loadStudents();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleRevokeLogin = async (s) => {
    const confirmed = window.confirm(`Revoke ${s.name}'s login? They won't be able to sign in until it's re-enabled.`);
    if (!confirmed) return;
    try {
      await studentService.revokeLogin(s.id);
      notify(`Login revoked for ${s.name}`, 'success');
      loadStudents();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleViewAs = (s) => {
    startImpersonation(s, 'student');
    navigate('/');
  };

  return (
    <div className="page">
      <h1>Students</h1>
      <div className="card">
        <p className="stat-note">
          Every customer in berlinr-backoffice is a student here — use "Enroll" to add one into a batch.
          Editing the email here updates it in berlinr-backoffice too, since it's the same customer record.
          A student can't sign in with Google until you click "Enable Login" for them, using the email above.
        </p>
        <div className="filter-row">
          <input
            type="search"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            <option value="paying">Paying</option>
            <option value="free_scheme">Free Scheme</option>
          </select>
          <select value={loginFilter} onChange={(e) => setLoginFilter(e.target.value)}>
            <option value="">All Login Statuses</option>
            <option value="enabled">Login Enabled</option>
            <option value="disabled">Login Not Enabled</option>
          </select>
          <select value={teacherFilter} onChange={(e) => setTeacherFilter(e.target.value)}>
            <option value="">All Teachers</option>
            {teachers.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <p className="stat-note">
          {filtered.length === students.length
            ? `${students.length} student${students.length === 1 ? '' : 's'}`
            : `Showing ${filtered.length} of ${students.length} students`}
        </p>
        {isAdmin && selectedIds.size > 0 && (
          <form className="inline-form enroll-form" onSubmit={handleBulkEnroll}>
            <span className="batch-meta">{selectedIds.size} selected</span>
            <select value={bulkBatchId} onChange={(e) => setBulkBatchId(e.target.value)}>
              <option value="">Select a batch…</option>
              {batches
                .filter((b) => b.status !== 'ended')
                .map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
            </select>
            <button type="submit" className="btn-primary" disabled={!bulkBatchId || bulkEnrolling}>
              {bulkEnrolling ? 'Enrolling…' : 'Enroll Selected'}
            </button>
            <button type="button" className="btn-link" onClick={clearSelection}>Clear selection</button>
          </form>
        )}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {isAdmin && (
                  <th>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                )}
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Level</th>
                <th>Type</th>
                <th>Batch</th>
                <th>Teacher</th>
                <th>Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                  <tr key={s.id}>
                    {isAdmin && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(s.id)}
                          onChange={() => toggleSelect(s.id)}
                          aria-label={`Select ${s.name}`}
                        />
                      </td>
                    )}
                    <td className="student-id-cell">{s.id}</td>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td>{s.levelInProgress || s.languageLevel || '—'}</td>
                    <td>
                      {s.customerType === 'paying' ? (
                        <span className="badge badge-paying">Paying</span>
                      ) : s.customerType === 'free_scheme' ? (
                        <span className="badge badge-free">Free Scheme</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {s.batches.length ? (
                        <span className="cell-truncate" title={s.batches.map((b) => b.name).join(', ')}>
                          {s.batches.map((b) => b.name).join(', ')}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {s.batches.length ? (
                        <span className="cell-truncate" title={s.batches.map((b) => b.teacher || 'No teacher yet').join(', ')}>
                          {s.batches.map((b) => b.teacher || 'No teacher yet').join(', ')}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <span className={`badge ${s.hasLogin ? 'badge-active' : 'badge-ended'}`}>
                        {s.hasLogin ? 'Enabled' : 'Not enabled'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <button className="btn-secondary" onClick={() => startEdit(s)}>Edit</button>
                        {s.hasLogin ? (
                          <button className="btn-secondary btn-danger" onClick={() => handleRevokeLogin(s)}>Revoke Login</button>
                        ) : (
                          <button className="btn-secondary" onClick={() => handleEnableLogin(s)}>Enable Login</button>
                        )}
                        {currentUser.isSuperAdmin && s.hasLogin && (
                          <button className="btn-secondary" onClick={() => handleViewAs(s)} title="View the portal as this student">
                            View as
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              )}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="stat-note">No students match "{search}".</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingId && (
        <Modal title={`Edit — ${students.find((s) => s.id === editingId)?.name || ''} (#${editingId})`} onClose={cancelEdit}>
          <form className="inline-form" onSubmit={(e) => handleSaveEmail(e, editingId)}>
            <input
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              placeholder="Email (leave blank to clear)"
              autoFocus
            />
            <button type="submit" className="btn-primary">Save</button>
          </form>

          {isAdmin && (
            <div className="modal-section">
              <h4>Batches</h4>
              {(() => {
                const editingStudent = students.find((s) => s.id === editingId);
                return (
                  <>
                    {editingStudent?.batches.length ? (
                      <ul className="list">
                        {editingStudent.batches.map((b) => (
                          <li key={b.id}>
                            <span>{b.name}</span>
                            <button className="btn-link btn-danger" onClick={() => handleUnenroll(editingId, b)}>Remove</button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="stat-note">Not enrolled in any batch.</p>
                    )}
                    <form className="inline-form" onSubmit={(e) => handleEnroll(e, editingId)}>
                      <select value={batchToEnroll} onChange={(e) => setBatchToEnroll(e.target.value)}>
                        <option value="">Select a batch…</option>
                        {batches
                          .filter((b) => b.status !== 'ended' && !editingStudent?.batches.some((sb) => sb.id === b.id))
                          .map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                      </select>
                      <button type="submit" className="btn-secondary" disabled={!batchToEnroll}>Add</button>
                    </form>
                  </>
                );
              })()}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-link" onClick={cancelEdit}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Students;
